/**
 * HistoryTab — SessionEvents scoped to this CIF, rendered as a vertical
 * timeline.  Uses `v3.events.list({ cifId })` when the Function supports it,
 * else falls back to a recent-window poll and filters client-side.
 */
import { useEffect, useState } from 'react';
import type { SessionEventDto } from '@retail-ops/shared-types';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Pill } from '@/components/primitives';
import { useApi } from '../hooks/useApi';
import { formatRelative } from '../utils/formatting';

export interface HistoryTabProps {
  cifId: string;
}

export function HistoryTab({ cifId }: HistoryTabProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const [events, setEvents] = useState<ReadonlyArray<SessionEventDto> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const signal = makeSignal();
    // Ask for all recent events; client-side filter for cif context.  This
    // is adequate for FSR scale and avoids depending on a server filter that
    // may not yet be implemented.
    const sinceIso = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString();
    client.retail.events.listSince(sinceIso, { top: 200 }, { signal }).then(
      (res): void => {
        const all = res.events ?? [];
        const scoped = all.filter((e) => {
          if (e.payloadJson) {
            try {
              const p = JSON.parse(e.payloadJson);
              if (p && typeof p === 'object' && (p as Record<string, unknown>)['cifId'] === cifId) return true;
            } catch { /* fall through */ }
          }
          return false;
        });
        setEvents(scoped);
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cifId]);

  if (error) return <EmptyState title="Unable to load history" message={error} />;
  if (events === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-9)' }}>
        <LoadingSpinner label="Loading audit history…" />
      </div>
    );
  }
  if (events.length === 0) {
    return <EmptyState title="No audit events captured yet" message="History will populate as officers interact with this CIF." />;
  }

  return (
    <div>
      {events.map((e) => (
        <div key={e.eventId} className="rcm-timeline-row">
          <span className="rcm-timeline-when">{formatRelative(e.timestamp)}</span>
          <div>
            <span className="rcm-timeline-what">{e.eventType}</span>
            {e.actor && <span style={{ marginLeft: 'var(--sp-3)', color: 'var(--ink-500)', fontSize: 11 }}>by {e.actor}</span>}
          </div>
          <Pill variant={e.severity === 'error' || e.severity === 'critical' ? 'err' : e.severity === 'warn' ? 'warn' : 'info'}>
            {e.eventCategory}
          </Pill>
        </div>
      ))}
    </div>
  );
}
